import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Search, Clock, Play, CheckCircle2, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    scheduled: {
      label: 'Scheduled',
      icon: Clock,
      className: 'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border-[rgba(59,130,246,0.35)]'
    },
    live: {
      label: 'LIVE',
      icon: Play,
      className: 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]'
    },
    finished: {
      label: 'Finished',
      icon: CheckCircle2,
      className: 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]'
    }
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.scheduled;
  const Icon = config.icon;

  return (
    <Badge className={`${config.className} border flex items-center gap-1`}>
      {status === 'live' && <span className="w-2 h-2 rounded-full bg-[#ef4444] animate-pulse-live" />}
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const MatchCard = ({ match }) => {
  const team1 = match.team1 || match.home_team || {};
  const team2 = match.team2 || match.away_team || {};
  const score1 = match.score1 ?? match.home_score ?? 0;
  const score2 = match.score2 ?? match.away_score ?? 0;
  const isLive = match.status?.toLowerCase() === 'live';
  const isFinished = match.status?.toLowerCase() === 'finished';

  return (
    <Link to={`/score/${match.id}`}>
      <Card className={`bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)] transition-all hover:translate-y-[-2px] cursor-pointer ${
        isLive ? 'ring-1 ring-[rgba(239,68,68,0.35)]' : ''
      }`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <StatusBadge status={match.status} />
            <ChevronRight className="h-5 w-5 text-[#8a93a3]" />
          </div>

          <div className="flex items-center justify-between">
            {/* Team 1 */}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#1d2230] flex items-center justify-center">
                  {team1.logo_url ? (
                    <img src={team1.logo_url} alt={team1.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <span className="text-[#f5c518] font-bold text-sm">{team1.short_name?.[0] || team1.name?.[0] || 'T1'}</span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-[#f0f2f7]">
                    {team1.short_name || team1.name || 'Team 1'}
                  </p>
                  <p className="text-xs text-[#8a93a3]">{team1.name || ''}</p>
                </div>
              </div>
            </div>

            {/* Score */}
            <div className="px-4 text-center">
              <div className={`text-2xl font-bold tabular-nums ${
                isLive ? 'text-[#f5c518]' : 'text-[#f0f2f7]'
              }`}>
                <span>{score1}</span>
                <span className="text-[#8a93a3] mx-2">:</span>
                <span>{score2}</span>
              </div>
              {match.sport && (
                <p className="text-xs text-[#8a93a3] mt-1">{match.sport}</p>
              )}
            </div>

            {/* Team 2 */}
            <div className="flex-1 text-right">
              <div className="flex items-center gap-3 justify-end">
                <div>
                  <p className="font-medium text-[#f0f2f7]">
                    {team2.short_name || team2.name || 'Team 2'}
                  </p>
                  <p className="text-xs text-[#8a93a3]">{team2.name || ''}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#1d2230] flex items-center justify-center">
                  {team2.logo_url ? (
                    <img src={team2.logo_url} alt={team2.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <span className="text-[#f5c518] font-bold text-sm">{team2.short_name?.[0] || team2.name?.[0] || 'T2'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Match Info */}
          <div className="mt-4 pt-3 border-t border-[#252a37] flex items-center justify-between text-xs text-[#8a93a3]">
            <span>{match.category || 'Singles'}</span>
            <span>
              {match.scheduled_at ? new Date(match.scheduled_at).toLocaleString() : 'TBD'}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default function ScorerMatchListPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchMatches();
    // Auto-refresh every 30s when live matches exist
    const interval = setInterval(() => {
      fetchMatches(true); // silent refresh
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug]);

  const fetchMatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/scorer/matches`);
      let scorerMatches = Array.isArray(response.data) ? response.data : response.data.items || [];

      // Enrich with team names from fixtures when missing
      const tourIds = [...new Set(scorerMatches.map(m => m.tournament_id).filter(Boolean))];
      if (tourIds.length > 0 && scorerMatches.some(m => !m.home_team?.name)) {
        const allFixtures = (await Promise.allSettled(
          tourIds.map(tid => api.get(`/orgs/${orgSlug}/tournaments/${tid}/fixtures`).then(r => Array.isArray(r.data) ? r.data : r.data.items || []))
        )).flatMap(r => r.status === 'fulfilled' ? r.value : []);

        const fxByMatchId = {};
        allFixtures.forEach(fx => { if (fx.match_id) fxByMatchId[fx.match_id] = fx; });

        scorerMatches = scorerMatches.map(m => {
          const fx = fxByMatchId[m.id];
          if (fx && !m.home_team?.name) {
            return {
              ...m,
              home_team: { ...m.home_team, name: fx.home?.name, short_name: fx.home?.short_name },
              away_team: { ...m.away_team, name: fx.away?.name, short_name: fx.away?.short_name },
              sport: m.sport || fx.sport_code?.replace(/_/g, ' '),
            };
          }
          return m;
        });
      }

      setMatches(scorerMatches);
    } catch (error) {
      // Fallback: try fetching fixtures from all tournaments
      try {
        const tournamentsRes = await api.get(`/orgs/${orgSlug}/tournaments`);
        const tournaments = Array.isArray(tournamentsRes.data) ? tournamentsRes.data : [];
        const results = await Promise.allSettled(
          tournaments.map((t) =>
            api.get(`/orgs/${orgSlug}/tournaments/${t.id}/fixtures`).then((r) => {
              const fixtures = Array.isArray(r.data) ? r.data : r.data.items || [];
              return fixtures;
            })
          )
        );
        const allMatches = results.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
        setMatches(allMatches);
      } catch {
        if (!silent) toast.error('Failed to load matches');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const filteredMatches = matches.filter(match => 
    match.team1?.name?.toLowerCase().includes(search.toLowerCase()) ||
    match.team2?.name?.toLowerCase().includes(search.toLowerCase()) ||
    match.home_team?.name?.toLowerCase().includes(search.toLowerCase()) ||
    match.away_team?.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort: live first, then scheduled, then finished
  const sortedMatches = [...filteredMatches].sort((a, b) => {
    const order = { live: 0, scheduled: 1, finished: 2 };
    return (order[a.status?.toLowerCase()] ?? 3) - (order[b.status?.toLowerCase()] ?? 3);
  });

  const liveCount = matches.filter(m => m.status?.toLowerCase() === 'live').length;

  return (
    <div className="p-4 sm:p-6" data-testid="scorer-match-list">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f0f2f7] mb-2">
          Select Match to Score
        </h1>
        <p className="text-[#8a93a3]">
          {liveCount > 0 && (
            <span className="text-[#ef4444] font-medium">{liveCount} live • </span>
          )}
          {matches.length} matches available
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by team name..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
            data-testid="score-match-search-input"
          />
        </div>
      </div>

      {/* Matches List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-4">
                <Skeleton className="h-6 w-20 bg-[#1d2230] mb-4" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full bg-[#1d2230]" />
                    <Skeleton className="h-4 w-24 bg-[#1d2230]" />
                  </div>
                  <Skeleton className="h-8 w-16 bg-[#1d2230]" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-24 bg-[#1d2230]" />
                    <Skeleton className="h-10 w-10 rounded-full bg-[#1d2230]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sortedMatches.length > 0 ? (
        <div className="space-y-4">
          {sortedMatches.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      ) : (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Target className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              {search ? 'No matches found' : 'No matches available'}
            </h3>
            <p className="text-[#8a93a3]">
              {search 
                ? 'Try adjusting your search' 
                : 'Matches will appear here once fixtures are created'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
